import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { Button } from "../primitives/Button";
import { IconButton } from "../primitives/IconButton";
import type { UiTheme } from "../theme";
import { ZOOM_MAX, ZOOM_MIN, ZOOM_STEP } from "./zoomPanModel";

// The zoom buttons both ZoomPanView halves show, always: pinch is only a
// shortcut on native (WCAG 2.5.1). The level is written out, so the state is
// never just a dimmed button.
export function ZoomControls({
  theme,
  zoom,
  onZoom,
}: {
  theme: UiTheme;
  zoom: number;
  onZoom: (next: number) => void;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  // 100 to 400: no grouping, and all five locales write Latin digits.
  const level = `${Math.round(zoom * 100)}%`;
  return (
    <View style={styles.row}>
      <IconButton
        theme={theme}
        name="minus"
        accessibilityLabel={t("bodymap.zoomOut")}
        disabled={zoom <= ZOOM_MIN}
        onPress={() => onZoom(zoom - ZOOM_STEP)}
      />
      <Text style={styles.level} accessibilityLiveRegion="polite">
        {level}
      </Text>
      <IconButton
        theme={theme}
        name="plus"
        accessibilityLabel={t("bodymap.zoomIn")}
        // No slop toward Reset, its neighbour: the two zones must not meet.
        hitSlop={{ top: 8, bottom: 8, left: 8 }}
        disabled={zoom >= ZOOM_MAX}
        onPress={() => onZoom(zoom + ZOOM_STEP)}
      />
      <Button
        theme={theme}
        variant="secondary"
        compact
        label={t("bodymap.zoomReset")}
        hitSlop={{ top: 8, bottom: 8, right: 8 }}
        disabled={zoom === ZOOM_MIN}
        onPress={() => onZoom(ZOOM_MIN)}
      />
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    row: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: s.s3,
    },
    level: {
      color: p.ink,
      fontFamily: ty.fonts.mono,
      fontSize: ty.sizes.md,
      fontVariant: ["tabular-nums"],
      minWidth: 52,
      textAlign: "center",
    },
  });
}
